// Angular
import { Injectable } from '@angular/core';
// RxJS
import { BehaviorSubject } from 'rxjs';
// Object path
import * as objectPath from 'object-path';
// Services
import { MenuConfigService } from './menu-config.service';
import { FavoritesComponent, FavoritesMenu, FavoritesSubMenu } from 'app/core/favorites/models/favorites.model';
import { FavoritesService } from 'app/core/favorites/_services/favorites.service';
import { environment } from 'environments/environment';
import { AuthorizationService } from 'app/core/ana/authorization';
import { finalize } from 'rxjs/operators';
import { ComponentsService } from 'app/core/ana/components';

@Injectable()
export class MenuAsideService {
	// Public properties
	menuList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	favoriteMenu: FavoritesMenu;
	count: number;
	isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	/**
	 * Service constructor
	 *
	 * @param menuConfigService: MenuConfigService
	 */
	constructor(private menuConfigService: MenuConfigService,
		private favoritesService: FavoritesService,
		private authorization: AuthorizationService,
		private componentsService: ComponentsService
	) {
		this.loadMenu();
	}

	/**
	 * Load menu list
	 */
	loadMenu() {
		this.favoriteMenu = new FavoritesMenu();
		this.favoriteMenu.title = 'Favorites';
		this.favoriteMenu.faIcon = 'fa fa-star';
		this.favoriteMenu.root = true;
		this.count = 0;

		this.favoritesService.findByUser(localStorage.getItem('user'))
			.pipe()
			.subscribe(
				data => {
					let favorites: Array<FavoritesComponent> = data as Array<FavoritesComponent>;
					//favorites = favorites.find(i => i.componentUrl.includes(""));
					if (favorites.length > 0) {
						favorites.forEach(element => {

							let link: FavoritesSubMenu = new FavoritesSubMenu();
							link.id = element.id;
							link.title = element.favoriteName;
							link.submenuIcon = true;
							link.page = this.getUrl(element.applicationName, element.componentUrl);
							if (element.applicationName == 'Supply Chain' || element.applicationName == 'Analytics') {
								link.faIcon = this.getIcon(element.applicationName);
							}
							else {
								link.icon = this.getIcon(element.applicationName);
							}
							this.getAuthorizationForMessages(element.applicationName, element.componentUrl, link, favorites)
						});
					} else {
						this.constructMenu();
					}
				}
			);
		// get menu list
	}

	getIcon(appName: string) {
		if (appName == 'O2C') { return 'attach_money' }
		else if (appName == 'Analytics') { return 'far fa-chart-bar' }
		else if (appName == 'Community Apps') { return 'fas fa-users' }
		else if (appName == 'Operations') { return 'directions_boat' }
		else if (appName == 'Supply Chain') { return 'fas fa-link' }
		else if (appName == 'CnC') { return 'fa fa-hourglass-half' }
		else if (appName == 'AnA') { return 'settings' }
		else if (appName == 'Dashboard') { return 'home' }
	}

	getUrl(appName: string, url: string) {
		if (appName == 'O2C') { return '/o2c/' + url }
		else if (appName == 'Analytics') { return '/analytics/' + url }
		else if (appName == 'Community Apps') { return '/community/' + url }
		else if (appName == 'Operations') { return '/operations/' + url }
		else if (appName == 'Supply Chain') { return '/supplyChain/' + url }
		else if (appName == 'CnC') { return '/cnc/' + url }
		else if (appName == 'AnA') { return '/ana/' + url }
		else if (appName == 'Dashboard') { return '/dashboard/' + url }
	}

	getApp(appName: string) {
		if (appName == 'O2C') { return 'o2c' }
		else if (appName == 'Analytics') { return 'analytics' }
		else if (appName == 'Community Apps') { return 'community' }
		else if (appName == 'Operations') { return 'operations' }
		else if (appName == 'Supply Chain') { return 'supplyChain' }
		else if (appName == 'CnC') { return 'cnc' }
		else if (appName == 'AnA') { return 'ana' }
		else if (appName == 'Dashboard') { return 'dashboard' }
	}

	getAuthorizationForMessages(appName: string, url: string, link: FavoritesSubMenu, favorites: Array<FavoritesComponent>): void {
		const username = localStorage.getItem('user');
		const componentOfNotifications = url;
		const appOfNotifications = environment.host + this.getApp(appName);
		this.authorization.isAuthorized(username, componentOfNotifications, appOfNotifications)
			.pipe(finalize(() => { }))
			.subscribe(
				isAuthorized => {
					this.count = this.count + 1;
					if (isAuthorized) {
						this.favoriteMenu.submenu.push(link);
						this.favoriteMenu.submenu.sort((a, b) => (a.id > b.id) ? 1 : -1);
					}
					if (this.count == favorites.length) {
						this.constructMenu();
					}
				}
			);
	}
	/**
	 * Method to create and renderize the menu
	 * @author Castor Transformación Digital - Andrés Montilla
	 * @since 2022-03-17
	 */
	constructMenu() {
		let menuItems: any[] = objectPath.get(this.menuConfigService.getMenus(), 'aside.items');
		menuItems.splice(1, 0, this.favoriteMenu);
		if (this.isMobile) {
			// Its mobile, we need to search only the mobile components
			this.componentsService.getAllComponents()
				.subscribe(data => {
					// Filter the data to get only the mobile components
					data = data.filter(component => component.mobileFlag == 'Y');
					for (const application of menuItems) {
						if (application.title == 'Dashboard') {
							if (!data.some(component => component.name == 'Dashboard')) {
								delete menuItems[menuItems.indexOf(application)];
							}
						}
						else if (application.title == 'Favorites') {
							for (const favorite of application.submenu) {
								if (!data.some(component => component.favName == favorite.title)) {
									delete application.submenu[application.submenu.indexOf(favorite)];
								}
							}
						} else if (application.section != 'Applications') {
							for (const module of application.submenu) {
								// Iterate the modules (Administration, processing, inquiries)
								if (module.submenu) {
									for (const submodule of module.submenu) {
										// Iterate the subModules or components (EDI, INVOICES or component (truck receipt, confirmation of sales))
										if (submodule.submenu) {
											for (const component of submodule.submenu) {
												// Iterate the final components (Customer addresses, inbound control, product mapping)
												if (component.page) {
													// Use only the module/component syntax (without application name)
													const pathSplitted = (component.page as string).split('/');
													const finalPath = '/' + pathSplitted[2] + '/' + pathSplitted[3];
													if (!data.some(component => component.component == finalPath)) {
														delete submodule.submenu[submodule.submenu.indexOf(component)];
													}
												}
											}
											// If the submodule has no components, remove it
											if (submodule.submenu.length == 0 || Object.keys(submodule.submenu).length == 0) {
												delete module.submenu[module.submenu.indexOf(submodule)];
											}
										} else {
											if (submodule.page) {
												// Use only the module/component syntax (without application name)
												const pathSplitted = (submodule.page as string).split('/');
												const finalPath = '/' + pathSplitted[2] + '/' + pathSplitted[3];
												if (!data.some(component => component.component == finalPath)) {
													delete module.submenu[module.submenu.indexOf(submodule)];
												}
											}
										}
									}
									// If the module has no submodules, remove it
									if (module.submenu.length == 0 || Object.keys(module.submenu).length == 0) {
										delete application.submenu[application.submenu.indexOf(module)];
									}
								}
							}
						}
					}
					this.menuList$.next(menuItems);
				});
		} else {
			this.menuList$.next(menuItems);
		}
	}
}
